import { Tooltip } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import {
    inputActionIds,
    inputActions,
    inputCategories,
    reservedInputs
} from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { InputCategory } from 'app/modules/wallforming/components/input-binding/types/inputCategory';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const chipClassName = 'flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors';

const chipStateClassName = (isSelected: boolean) =>
    isSelected
        ? 'border-border-accent bg-blue-50 text-text-accent'
        : 'border-border-ultra-light text-text-secondary hover:border-border-strong';

/** Filters the rows by binding context. */
export const InputContextFilter = () => {
    const { t } = useTranslation();
    const { selectedCategory, setSelectedCategory, selectedContext, bindings } = usePlacementStore(
        useShallow((state) => ({
            selectedCategory: state.selectedInputCategory,
            setSelectedCategory: state.setSelectedInputCategory,
            selectedContext: state.inputContextFilter,
            bindings: state.inputBindings
        }))
    );
    const availableCategories = useMemo(() => {
        const categories = new Set<InputCategory>();
        for (const actionId of inputActionIds) {
            const action = inputActions[actionId];
            if (action.contexts.includes(selectedContext)) {
                categories.add(action.category);
            }
        }
        for (const input of reservedInputs) {
            if (input.contexts.includes(selectedContext)) {
                categories.add(input.category);
            }
        }
        return (Object.keys(inputCategories) as InputCategory[]).filter((category) => categories.has(category));
    }, [selectedContext]);

    useEffect(() => {
        if (!availableCategories.includes(selectedCategory)) {
            setSelectedCategory(availableCategories[0]);
        }
    }, [availableCategories, selectedCategory, setSelectedCategory]);

    const unassignedCounts = useMemo(() => {
        const counts: Partial<Record<InputCategory, number>> = {};
        for (const actionId of inputActionIds) {
            if (bindings.slots[actionId].every((chord) => chord === null)) {
                const { category } = inputActions[actionId];
                counts[category] = (counts[category] ?? 0) + 1;
            }
        }
        return counts;
    }, [bindings]);

    return (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-border-ultra-light px-4 py-2">
            {availableCategories.map((category) => {
                const { labelKey, icon } = inputCategories[category];
                return (
                    <button
                        key={category}
                        className={`${chipClassName} ${chipStateClassName(selectedCategory === category)}`}
                        type="button"
                        onClick={() => setSelectedCategory(category)}
                    >
                        <Icon className="icon-xs" icon={icon} />
                        {t(labelKey)}
                        {unassignedCounts[category] && (
                            <Tooltip
                                label={t('labelKeyBindingUnassignedCount', { count: unassignedCounts[category] })}
                                zIndex={20000}
                            >
                                <span className="rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-900">
                                    {unassignedCounts[category]}
                                </span>
                            </Tooltip>
                        )}
                    </button>
                );
            })}
        </div>
    );
};
